import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Users, Trophy, Percent, BellRinging, Phone, AddressBook, Package, Warehouse, ArrowRight } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/lib/access";
import { getErrorMessage } from "@/lib/auth";

const API = `${window.location.origin}/api`;

function StatCard({ label, value, icon: Icon, tone, testId }) {
  return (
    <Card className="p-4 border border-gray-200 bg-white" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tone}`}><Icon size={20} weight="bold" /></div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">{label}</p>
          <p className="text-2xl font-black text-gray-900" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{value}</p>
        </div>
      </div>
    </Card>
  );
}

function FollowupList({ title, items, tone, testId }) {
  return (
    <Card className="p-4 border border-gray-200 bg-white" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h3 className={`text-sm font-bold ${tone}`}>{title}</h3>
        <span className="text-xs font-bold text-gray-500" data-testid={`${testId}-count`}>{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-gray-400 py-6 text-center">Nothing here</p>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {items.map((f) => (
            <div key={f.lead_id} className="p-2.5 rounded-lg border border-gray-100 bg-gray-50" data-testid={`followup-${f.lead_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{f.client_name}</p>
                  <p className="text-xs text-gray-500 truncate">{f.company_name || f.product_interested || "-"}</p>
                </div>
                <span className="text-[11px] font-mono text-gray-500 flex-shrink-0">{f.follow_up_date}</span>
              </div>
              {f.phone && (
                <a href={`tel:${f.phone}`} className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline" data-testid={`call-${f.lead_id}`}>
                  <Phone size={12} /> {f.phone}
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

const SHORTCUTS = [
  { path: "/leads", label: "CRM Leads", icon: Users },
  { path: "/customers", label: "Customers", icon: AddressBook },
  { path: "/product-rates", label: "Product Rates", icon: Package },
  { path: "/inventory", label: "Stock", icon: Warehouse },
];

export default function SalesDashboardPage() {
  const user = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [followups, setFollowups] = useState({ overdue: [], today: [], upcoming: [] });

  useEffect(() => {
    Promise.allSettled([axios.get(`${API}/crm/analytics`), axios.get(`${API}/crm/followups`)]).then(([a, f]) => {
      if (a.status === "fulfilled") setAnalytics(a.value.data);
      if (f.status === "fulfilled") setFollowups(f.value.data);
      const failed = [a, f].find((r) => r.status === "rejected");
      if (failed && failed.reason?.response?.status !== 401) toast.error(getErrorMessage(failed.reason));
    });
  }, []);

  return (
    <div className="space-y-5 max-w-7xl" data-testid="sales-dashboard-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-black text-gray-900" style={{ fontFamily: "'Gupter', serif" }}>Hello, {user?.name || "there"}</h1>
        <p className="text-xs font-bold text-indigo-600">Your leads and follow-ups at a glance</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total Leads" value={analytics?.total_leads ?? "-"} icon={Users} tone="bg-indigo-50 text-indigo-700" testId="sales-stat-total-leads" />
        <StatCard label="Pending Follow-ups" value={analytics?.pending_followups ?? "-"} icon={BellRinging} tone="bg-amber-50 text-amber-700" testId="sales-stat-pending" />
        <StatCard label="Closed Won" value={analytics?.closed_won ?? "-"} icon={Trophy} tone="bg-green-50 text-green-700" testId="sales-stat-won" />
        <StatCard label="Conversion" value={analytics ? `${analytics.conversion_rate}%` : "-"} icon={Percent} tone="bg-blue-50 text-blue-700" testId="sales-stat-conversion" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {SHORTCUTS.map((s) => (
          <button key={s.path} onClick={() => navigate(s.path)} className="group flex items-center justify-between gap-2 p-3 rounded-xl border-2 border-indigo-100 bg-white hover:border-indigo-400 hover:shadow-md transition-[border-color,box-shadow]" data-testid={`shortcut-${s.path.slice(1)}`}>
            <span className="flex items-center gap-2 text-sm font-bold text-gray-800"><s.icon size={18} className="text-indigo-600" weight="bold" /> {s.label}</span>
            <ArrowRight size={14} className="text-indigo-400 group-hover:translate-x-0.5 transition-transform" />
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <FollowupList title="Overdue" items={followups.overdue} tone="text-red-600" testId="followups-overdue" />
        <FollowupList title="Today" items={followups.today} tone="text-amber-600" testId="followups-today" />
        <FollowupList title="Upcoming" items={followups.upcoming} tone="text-indigo-600" testId="followups-upcoming" />
      </div>
    </div>
  );
}
